import * as React from 'react';

import { Skeleton, Row as AntdRow, Col } from 'antd';
import { Column, Row, Space, Text } from 'farmhub-ui-core';

import { useAppSelector } from 'app/hooks';
import { env } from 'env';
import { selectAppState } from 'slice';
import {
  formatAppearPhoneNumber,
  formatDate,
  formatMoney,
  getFullAddress,
  getOrderItemQuantityByStatus,
} from 'utils/function';

import Paragraph from '../components/Paragraph';
import ExpandTable from './components/ExpandTable';
import GroupProductTable from './components/GroupProductTable';
import { CQRCode, WrapperContent } from './styles';

interface PaymentBillContentProps {
  order: any;
  isLoading: boolean;
  columnsVisible?: {
    [column: string]: boolean;
  };
  informationVisible?: any;
  padding?: string;
  onChangeOrderInfo?: (val: any) => void;
  onCreateImage?: () => void;
}

const PaymentBillContent = ({
  order,
  isLoading,
  columnsVisible,
  informationVisible,
  padding,
  onChangeOrderInfo,
  onCreateImage,
}: PaymentBillContentProps): JSX.Element => {
  const app = useAppSelector(selectAppState);
  const user = useAppSelector(state => state.auth.item);
  const [orderInfo, setOrderInfo] = React.useState<any>({});

  const handleUpdateData = (val: any) => {
    if (typeof val === 'object' && val !== null) {
      onChangeOrderInfo && onChangeOrderInfo(val);
      setOrderInfo((prevState: any) => {
        return {
          ...prevState,
          ...val,
        };
      });
      onCreateImage && onCreateImage();
    }
  };

  const promotion = React.useMemo(
    () =>
      order?.additionalInfo?.promotions &&
      order?.additionalInfo?.promotions.length > 0
        ? order?.additionalInfo?.promotions[0]
        : undefined,
    [order]
  );

  const formatData = React.useMemo(() => {
    return {
      ...order,
      entitySubCategories: informationVisible.Category
        ? (order?.entitySubCategories ?? [])
            .map((entitySubCategory: any) => {
              return {
                ...entitySubCategory,
                orderItems: entitySubCategory.orderItems.filter(
                  (orderItem: any) =>
                    informationVisible?.Quantity
                      ? getOrderItemQuantityByStatus({
                          orderItem,
                          status: order.status,
                          trackingStatus: order.trackingStatus,
                        }) > 0
                      : true
                ),
              };
            })
            .filter(
              (entitySubCategory: any) =>
                entitySubCategory.orderItems.length > 0
            )
        : [
            {
              name: 'Danh mục',
              orderItems: (order?.orderItems ?? []).filter((orderItem: any) =>
                informationVisible?.Quantity
                  ? getOrderItemQuantityByStatus({
                      orderItem,
                      status: order.status,
                      trackingStatus: order.trackingStatus,
                    }) > 0
                  : true
              ),
            },
          ],
    };
  }, [order, informationVisible]);

  return (
    <WrapperContent className="bill-content" padding={padding}>
      <Column alignItems="center" width="100%">
        <Text
          color="black"
          size="md"
          textTransform="uppercase"
          weight="extraBold"
        >
          HÓA ĐƠN BÁN HÀNG
        </Text>
      </Column>
      <Space height={24} />
      <AntdRow gutter={[12, 12]}>
        <Col span={10}>
          <Column styles={{ gap: 2 }} width="100%">
            {isLoading ? (
              <>
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Space height={3} />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
              </>
            ) : (
              <>
                <Row alignItems="flex-start" width="100%">
                  <Text
                    color="black"
                    lineHeight="20px"
                    size="xxs"
                    styles={{ width: 100, flexShrink: 0 }}
                    weight="bold"
                  >
                    Mã hóa đơn:
                  </Text>
                  <Text
                    color="black"
                    lineHeight="20px"
                    size="xxs"
                    styles={{ flexShrink: 0 }}
                    weight="bold"
                  >
                    {order?.code ?? 'Đơn tạm'}
                  </Text>
                </Row>
                <Row alignItems="flex-start" width="100%">
                  <Text
                    color="black"
                    lineHeight="20px"
                    size="xxs"
                    styles={{ width: 100, flexShrink: 0 }}
                    weight="bold"
                  >
                    Nhân viên:
                  </Text>
                  <Paragraph
                    size="xxs"
                    weight="bold"
                    onSubmit={(e: any) => {
                      handleUpdateData({ employee: e });
                    }}
                  >
                    {orderInfo?.employee ?? (user?.displayName || '')}
                  </Paragraph>
                </Row>
                <Row alignItems="flex-start" width="100%">
                  <Text
                    color="black"
                    lineHeight="20px"
                    size="xxs"
                    styles={{ width: 100, flexShrink: 0 }}
                    weight="bold"
                  >
                    Khách hàng:
                  </Text>
                  <Paragraph
                    size="xxs"
                    weight="bold"
                    onSubmit={(e: any) => {
                      handleUpdateData({ customerName: e });
                    }}
                  >
                    {orderInfo?.customerName ??
                      (order.customer?.displayName || 'Ẩn danh')}
                  </Paragraph>
                </Row>
                {informationVisible?.Address && (
                  <Row alignItems="flex-start" width="100%">
                    <Text
                      color="black"
                      lineHeight="20px"
                      size="xxs"
                      styles={{ width: 100, flexShrink: 0 }}
                      weight="bold"
                    >
                      Địa chỉ:
                    </Text>
                    <Paragraph
                      size="xxs"
                      weight="bold"
                      onSubmit={(e: any) => {
                        handleUpdateData({ address: e });
                      }}
                    >
                      {orderInfo?.address ??
                        (getFullAddress(
                          order.additionalInfo?.address?.address,
                          order.additionalInfo?.address?.district?.fullName,
                          order.additionalInfo?.address?.province?.fullName,
                          order.additionalInfo?.address?.ward?.fullName
                        ) ||
                          '')}
                    </Paragraph>
                  </Row>
                )}
              </>
            )}
          </Column>
        </Col>
        <Col span={10}>
          <Column styles={{ gap: 2 }} width="100%">
            {isLoading ? (
              <>
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Space height={3} />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
              </>
            ) : (
              <>
                <Row alignItems="flex-start" width="100%">
                  <Text
                    color="black"
                    lineHeight="20px"
                    size="xxs"
                    styles={{ width: 100, flexShrink: 0 }}
                    weight="bold"
                  >
                    Ngày:
                  </Text>
                  <Paragraph
                    size="xxs"
                    weight="bold"
                    onSubmit={(e: any) => {
                      handleUpdateData({ createdAt: e });
                    }}
                  >
                    {orderInfo?.createdAt ??
                      `Ngày ${formatDate(
                        order.createdAt,
                        'DD'
                      )} tháng ${formatDate(
                        order.createdAt,
                        'MM'
                      )} năm ${formatDate(order.createdAt, 'YYYY')}`}
                  </Paragraph>
                </Row>
                <Row alignItems="flex-start" width="100%">
                  <Text
                    color="black"
                    lineHeight="20px"
                    size="xxs"
                    styles={{ width: 100, flexShrink: 0 }}
                    weight="bold"
                  >
                    Thu ngân:
                  </Text>
                  <Paragraph
                    size="xxs"
                    weight="bold"
                    onSubmit={(e: any) => {
                      handleUpdateData({ cashier: e });
                    }}
                  >
                    {orderInfo?.cashier ?? ''}
                  </Paragraph>
                </Row>
                <Row alignItems="flex-start" width="100%">
                  <Text
                    color="black"
                    lineHeight="20px"
                    size="xxs"
                    styles={{ width: 100, flexShrink: 0 }}
                    weight="bold"
                  >
                    SĐT:
                  </Text>
                  <Paragraph
                    size="xxs"
                    weight="bold"
                    onSubmit={(e: any) => {
                      handleUpdateData({ phoneNumber: e });
                    }}
                  >
                    {orderInfo?.phoneNumber ??
                      (formatAppearPhoneNumber(
                        order?.additionalInfo?.address?.phoneNumber
                      ) ||
                        '')}
                  </Paragraph>
                </Row>
              </>
            )}
          </Column>
        </Col>
        <Col span={4}>
          <Row justifyContent="flex-end" styles={{ gap: 12 }} width="100%">
            {isLoading ? (
              <Skeleton.Input style={{ width: 64, height: 64 }} />
            ) : order?.code ? (
              <CQRCode
                level="L"
                value={`${
                  app?.domains?.hubBilling?.domain ??
                  env.service.hubbillingWebApp
                }/${order.code}`}
              />
            ) : (
              <></>
            )}
          </Row>
        </Col>
      </AntdRow>
      <Space height={18} />

      <GroupProductTable
        columnsVisible={columnsVisible}
        informationVisible={informationVisible}
        isLoading={isLoading}
        order={formatData}
      />
      <Space height={18} />
      <AntdRow gutter={[12, 12]}>
        <Col span={8}>
          <Column styles={{ gap: 6 }}>
            {isLoading ? (
              <>
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
              </>
            ) : (
              <>
                <Text color="black" size="xxs" weight="extraBold">
                  Thông tin thanh toán
                </Text>
                <Text color="black" size="xxs">
                  Tên ngân hàng:{' '}
                  <Text
                    color="black"
                    size="xxs"
                    styles={{
                      fontStyle: order?.additionalInfo?.bankAccount
                        ? 'normal'
                        : 'italic',
                    }}
                  >
                    {order?.additionalInfo?.bankAccount
                      ? order?.additionalInfo?.bankAccount.bank.tradingName
                      : 'Chưa cập nhật'}
                  </Text>
                </Text>
                <Text color="black" size="xxs">
                  Số tài khoản:{' '}
                  <Text
                    color="black"
                    size="xxs"
                    styles={{
                      fontStyle: order?.additionalInfo?.bankAccount
                        ? 'normal'
                        : 'italic',
                    }}
                  >
                    {order?.additionalInfo?.bankAccount
                      ? order?.additionalInfo?.bankAccount.number
                      : 'Chưa cập nhật'}
                  </Text>
                </Text>
                <Text color="black" size="xxs">
                  Chủ tài khoản:{' '}
                  <Text
                    color="black"
                    size="xxs"
                    styles={{
                      fontStyle: order?.additionalInfo?.bankAccount
                        ? 'normal'
                        : 'italic',
                    }}
                  >
                    {order?.additionalInfo?.bankAccount
                      ? order?.additionalInfo?.bankAccount.name
                      : 'Chưa cập nhật'}
                  </Text>
                </Text>
                <Text color="black" size="xxs">
                  Ghi chú:{' '}
                  <Text
                    color="black"
                    size="xxs"
                    styles={{
                      fontStyle: order?.additionalInfo?.bankAccount
                        ? 'normal'
                        : 'italic',
                    }}
                  >
                    {order?.additionalInfo?.bankAccount
                      ? order?.additionalInfo?.bankAccount.note
                      : 'Chưa cập nhật'}
                  </Text>
                </Text>
              </>
            )}
          </Column>
        </Col>
        <Col span={8}>
          <Column styles={{ gap: 6 }}>
            {isLoading ? (
              <>
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
              </>
            ) : (
              <>
                <Text color="black" size="xxs" weight="extraBold">
                  Thông tin vận chuyển
                </Text>
                {order?.additionalInfo?.transportNote ? (
                  <>
                    {(
                      order?.additionalInfo?.transportNote.description ?? []
                    ).map((item: any) => (
                      <Text color="black">{item.value}</Text>
                    ))}
                  </>
                ) : (
                  <>
                    <Text color="black" size="xxs">
                      Đơn vị vận chuyển:{' '}
                      <Text
                        color="primary"
                        size="xxs"
                        styles={{
                          fontStyle: 'italic',
                        }}
                      >
                        Chưa cập nhật
                      </Text>
                    </Text>
                    <Text color="black" size="xxs">
                      Số điện thoại:{' '}
                      <Text
                        color="primary"
                        size="xxs"
                        styles={{
                          fontStyle: 'italic',
                        }}
                      >
                        Chưa cập nhật
                      </Text>
                    </Text>
                    <Text color="black" size="xxs">
                      Tên tài xế:{' '}
                      <Text
                        color="primary"
                        size="xxs"
                        styles={{
                          fontStyle: 'italic',
                        }}
                      >
                        Chưa cập nhật
                      </Text>
                    </Text>
                  </>
                )}
              </>
            )}
          </Column>
        </Col>
        <Col span={8}>
          <Column styles={{ gap: 6 }}>
            {isLoading ? (
              <>
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
                <Skeleton.Input
                  size="small"
                  style={{ width: '120px', alignItems: 'center' }}
                />
              </>
            ) : (
              <>
                <Row justifyContent="space-between" width="100%">
                  <Text color="black" size="xxs" weight="regular">
                    Tạm tính:
                  </Text>
                  <Text color="black" size="xxs" weight="bold">
                    {formatMoney(
                      Number(order?.totalPrice) +
                        Number(order?.totalAdjustment) -
                        Number(order?.deliveryFee)
                    )}
                    đ
                  </Text>
                </Row>
                <Row justifyContent="space-between" width="100%">
                  <Text color="black" size="xxs" weight="regular">
                    Phí giao hàng:
                  </Text>
                  <Text color="black" size="xxs" weight="bold">
                    {formatMoney(order?.deliveryFee ?? 0)}đ
                  </Text>
                </Row>
                <Row justifyContent="space-between" width="100%">
                  <Row styles={{ gap: 3 }} width="fit-content">
                    <Text color="black" size="xxs" weight="regular">
                      Giảm giá:
                    </Text>
                  </Row>
                  <Text color="black" size="xxs" weight="bold">
                    {formatMoney(
                      order.totalAdjustment -
                        (promotion ? promotion?.amount : 0) >
                        0
                        ? order.totalAdjustment -
                            (promotion ? promotion?.amount : 0)
                        : 0
                    )}
                    đ
                  </Text>
                </Row>

                {promotion && (
                  <Row justifyContent="space-between" width="100%">
                    <Row styles={{ gap: 3 }} width="fit-content">
                      <Text color="black" size="xxs" weight="regular">
                        {promotion.name}:{' '}
                        {promotion?.rate && promotion?.rate > 0
                          ? `(${promotion?.rate}%)`
                          : ``}
                      </Text>
                    </Row>
                    <Text color="black" size="xxs" weight="bold">
                      {formatMoney(promotion?.amount ?? 0)}đ
                    </Text>
                  </Row>
                )}

                <Row justifyContent="space-between" width="100%">
                  <Text color="black" size="xxs" weight="bold">
                    Tổng tiền:
                  </Text>
                  <Text size="xs" weight="extraBold">
                    {formatMoney(order?.totalPrice ?? 0)}đ
                  </Text>
                </Row>
              </>
            )}
          </Column>
        </Col>
      </AntdRow>
      <Space height={24} />
      <Column alignItems="center" width="100%">
        <Text color="black" size="sm" weight="extraBold">
          Trân trọng cảm ơn!
        </Text>
      </Column>
    </WrapperContent>
  );
};

export default PaymentBillContent;
